import React, { useState, useEffect } from 'react';
import { AccountTree } from '@mui/icons-material';
import { formatAppDate } from '../services/appConfigRuntime';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    TextField,
    Typography,
    Box,
    IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { toast } from 'react-toastify';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/tenant-link-relationships`;

const TenantLinkRelationship = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [tenantLinks, setTenantLinks] = useState([]);
    const [availableLinks, setAvailableLinks] = useState([]);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedLinkId, setSelectedLinkId] = useState('');
    const [tenantId, setTenantId] = useState(null);

    useEffect(() => {
        const storedTenantId = localStorage.getItem('tenantId');
        if (storedTenantId) {
            setTenantId(parseInt(storedTenantId));
        }
    }, []);

    useEffect(() => {
        if (tenantId) {
            fetchTenantLinks();
            fetchAvailableLinks();
        }
    }, [tenantId]);

    const fetchTenantLinks = async () => {
        if (!tenantId) return [];

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Map to include id field for DynamicContainer
            const mappedData = data.map(item => ({
                ...item,
                id: item.tenantLinkId
            }));

            setTenantLinks(mappedData);
            return mappedData;
        } catch (error) {
            console.error('Error fetching tenant links:', error);
            toast.error(`Error fetching your links: ${error.message}`);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAvailableLinks = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/available`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAvailableLinks(data);
        } catch (error) {
            console.error('Error fetching available links:', error);
            toast.error(`Error fetching available links: ${error.message}`);
        }
    };

    const handleOpenAddDialog = () => {
        // Directly open our custom dropdown dialog
        setAddDialogOpen(true);
    };

    const handleAddLink = async () => {
        if (!selectedLinkId) {
            toast.warning('Please select a link');
            return;
        }

        if (!tenantId) {
            toast.error('Tenant ID not found');
            return;
        }

        try {
            const username = localStorage.getItem('username') || 'user';
            const token = localStorage.getItem('token');

            const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/links/${selectedLinkId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ addedBy: username })
            });

            if (!response.ok) {
                if (response.status === 400) {
                    throw new Error('This link is already added to your selections');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast.success('Link added successfully');
            setAddDialogOpen(false);
            setSelectedLinkId('');
            await fetchTenantLinks();
        } catch (error) {
            console.error('Error adding link:', error);
            toast.error(`Failed to add link: ${error.message}`);
        }
    };

    const handleDelete = async (id) => {
        if (!tenantId) return;

        // Find the link to get its linkHeaderId
        const link = tenantLinks.find(l => l.id === id);
        if (!link) {
            toast.error('Link not found');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}/links/${link.linkHeaderId}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast.success('Link removed successfully');
            await fetchTenantLinks();
            return true;
        } catch (error) {
            console.error('Error removing link:', error);
            toast.error(`Failed to remove link: ${error.message}`);
            throw error;
        }
    };

    const handleCloseDialog = () => {
        setAddDialogOpen(false);
        setSelectedLinkId('');
    };

    // Filter out links that are already added
    const getAvailableLinksForDropdown = () => {
        const addedLinkIds = new Set(tenantLinks.map(tl => tl.linkHeaderId));
        return availableLinks.filter(link => !addedLinkIds.has(link.linkHeaderId));
    };

    const columns = [
        { field: 'linkName', headerName: 'Link Name', width: 250 },
        { field: 'domain', headerName: 'Domain', width: 150 },
        { field: 'description', headerName: 'Description', width: 300 },
        { field: 'primaryTableName', headerName: 'Primary Table', width: 180 },
        { field: 'usageScope', headerName: 'Usage Scope', width: 120 },
        { field: 'addedBy', headerName: 'Added By', width: 150 },
        {
            field: 'addedAt',
            headerName: 'Added At',
            width: 180,
            valueGetter: (params) => {
                const value = params?.value || params?.row?.addedAt;
                return value ? formatAppDate(value) : '';
            }
        }
    ];

    return (
        <>
            <DynamicContainer
                title="My Link Relationships"
                icon={AccountTree}
                columns={columns}
                formFields={[]} // No form fields needed
                initialFormData={{}}
                idField="id"
                rows={tenantLinks}
                onAddClick={handleOpenAddDialog} // Use onAddClick instead of onCreate
                onDelete={handleDelete}
                isLoading={isLoading}
                functionCode="Global Link Relationships"
                disableEdit={true} // Disable edit since we can't edit global links
            />

            {/* Add Link Dialog */}
            <Dialog open={addDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Add Link Relationship
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            select
                            label="Select Link"
                            value={selectedLinkId}
                            onChange={(e) => setSelectedLinkId(e.target.value)}
                            helperText="Choose a link relationship to add to your workspace"
                        >
                            {getAvailableLinksForDropdown().length === 0 ? (
                                <MenuItem disabled>No more links available</MenuItem>
                            ) : (
                                getAvailableLinksForDropdown().map((link) => (
                                    <MenuItem key={link.linkHeaderId} value={link.linkHeaderId}>
                                        {link.linkName} ({link.domain})
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAddLink}
                        disabled={!selectedLinkId}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TenantLinkRelationship;
